/**
 * TODO: Recalculate position and placement when resizing window
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { useLocation } from 'react-router-dom';

import { useClickOutside } from 'hooks';
import Typography from 'components/ui/Typography';
import { useLayoutScrollContainer } from 'contexts/LayoutScrollContainerContext';

import css from './Popover.module.scss';

type Placement = 'top' | 'right' | 'bottom' | 'left';

export interface PopoverProps extends React.PropsWithChildren {
    content: React.ReactNode;
    color?: 'white' | 'red' | 'gray';
    hideTriangle?: boolean;
    visible?: boolean;
    placement?: Placement;

    // When 'frozen', visibility toggle will not work at all, so the initial
    // visibility will be preserved (todo: this is a bit confusing -- maybe
    // rename to 'disableVisibilityToggle' or similar)
    frozen?: boolean;

    ignoreClicks?: boolean;
    padding?: 'S' | 'M' | 'L';
    className?: string;
    offsetVertical?: number;
    offsetHorizontal?: number;
    onOpen?: () => void;
    onClose?: () => void;
    allowScroll?: boolean;
    scrollContainer?: HTMLElement | null;
}

interface MenuProps extends React.PropsWithChildren {
    direction?: 'column' | 'row';
}

interface MenuItemProps extends React.PropsWithChildren {
    onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    beforeContent?: React.ReactNode;
    afterContent?: React.ReactNode;
    className?: string;
}

const calculatePosition = (
    targetRect: DOMRect,
    contentRect: DOMRect,
    placement: Placement,
    offsetHorizontal: number,
    offsetVertical: number,
) => {
    let [x, y] = [0, 0];

    if (window.innerHeight < targetRect.y + targetRect.height + contentRect.height + 8) {
        placement = 'top';
    }

    switch (placement) {
        case 'top':
            // x is the middle of the target
            x = targetRect.x + offsetHorizontal - (contentRect.width - targetRect.width) / 2;
            // y is the top of the target minus the height of the content minus 8px
            y = targetRect.y + offsetVertical - contentRect.height - 8;
            break;
        case 'right':
            // x is the right of the target plus 12px
            x = targetRect.x + offsetHorizontal + targetRect.width + 12;
            // y is the middle of the target
            y = targetRect.y + offsetVertical - contentRect.height / 2 + targetRect.height / 2;

            break;
        case 'bottom':
            // x is the middle of the target
            x = targetRect.x + offsetHorizontal - (contentRect.width - targetRect.width) / 2;
            // y is the bottom of the target plus 8px
            y = targetRect.y + offsetVertical + targetRect.height + 8;

            break;
        case 'left':
            // x is the left of the target minus the width of the content minus 12px
            x = targetRect.x + offsetHorizontal - contentRect.width - 12;
            // y is the middle of the target
            y = targetRect.y + offsetVertical - contentRect.height / 2 + targetRect.height / 2;

            break;
        default:
            break;
    }

    return { x, y };
};

const shiftPopoverIntoViewport = (
    x: number,
    y: number,
    contentRect: DOMRect,
): { x: number; y: number; changed: boolean } => {
    const { innerWidth, innerHeight } = window;

    let [newX, newY] = [x, y];

    if (x < 0) {
        newX = 0;
    } else if (x + contentRect.width > innerWidth) {
        newX = innerWidth - contentRect.width;
    }

    if (y < 0) {
        newY = 0;
    } else if (y + contentRect.height > innerHeight) {
        newY = innerHeight - contentRect.height;
    }

    return { x: newX, y: newY, changed: newX !== x || newY !== y };
};

const Popover: React.FC<PopoverProps> & { Menu: React.FC<MenuProps> } & { MenuItem: React.FC<MenuItemProps> } = ({
    color = 'white',
    visible = false,
    placement = 'bottom',
    frozen = false,
    ignoreClicks = false,
    hideTriangle = false,
    content,
    padding,
    children,
    className,
    offsetHorizontal = 0,
    offsetVertical = 0,
    allowScroll = false,
    scrollContainer,
    onOpen,
    onClose,
}) => {
    const targetRef = useRef<HTMLDivElement>(null) as React.MutableRefObject<HTMLDivElement>;
    const contentRef = useRef<HTMLDivElement>(null) as React.MutableRefObject<HTMLDivElement>;
    const [targetPosition, setTargetPosition] = useState<{ x: number; y: number } | undefined>(undefined);
    const [popoverPlacement, setPopoverPlacement] = useState(placement);
    const [isVisible, setIsVisible] = useState(visible);
    const [isTriangleHidden, setIsTriangleHidden] = useState<boolean>(hideTriangle);

    const location = useLocation();
    const { scrollContainer: layoutScrollContainer } = useLayoutScrollContainer();
    const currentScrollContainer = scrollContainer || layoutScrollContainer;

    useEffect(() => {
        setIsVisible(visible);
    }, [visible]);

    // Visibility can change between mouse down and mouse up (due to click outside). In order for button menu
    // toggling to work correctly, only a single visibility change should occur per click.
    const [visibilityChangedSinceMouseDown, setVisibilityChangedSinceMouseDown] = useState<boolean>(false);

    const calculate = () => {
        const targetRect = targetRef?.current?.getBoundingClientRect();
        const contentRect = contentRef?.current?.getBoundingClientRect();

        if (!targetRect && !contentRect) return;

        if (contentRect) {
            const { x, y } = calculatePosition(targetRect, contentRect, placement, offsetHorizontal, offsetVertical);

            const { x: shiftedX, y: shiftedY, changed } = shiftPopoverIntoViewport(x, y, contentRect);

            // We don't want the popover to leave the visible window area, so we shift it back
            // into the viewport, but since the popover is absolutely positioned, this means that
            // the triangle will no longer be aligned with the target. To fix this, we "hide" the
            // triangle if the popover was shifted.
            // Todo: Find a better solution, move the triangle appropriately
            if (changed) {
                setIsTriangleHidden(true);
            }

            setTargetPosition({ x: shiftedX, y: shiftedY });
            setPopoverPlacement(placement);
        }
    };

    const toggle = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        // block event bubbling
        e.stopPropagation();

        if (!frozen) {
            visibilityChangedSinceMouseDown || setIsVisible(!isVisible);
        }
    };

    const hide = () => {
        if (!frozen) {
            setIsVisible(false);
            onClose && onClose();
        }
    };

    const resize = useCallback(() => {
        calculate();
    }, [targetRef, contentRef, content]);

    const positionStyle = targetPosition ? { top: targetPosition.y, left: targetPosition.x } : undefined;

    useEffect(() => {
        setPopoverPlacement(placement);

        allowScroll || currentScrollContainer.addEventListener('scroll', hide);
        window.addEventListener('resize', resize);

        // Close popover when a link is followed
        hide();

        return () => {
            allowScroll || currentScrollContainer.removeEventListener('scroll', hide);
            document.removeEventListener('scroll', calculate);
        };
    }, [location]);

    useLayoutEffect(() => {
        if (isVisible) {
            calculate();
            onOpen && onOpen();
        }

        setVisibilityChangedSinceMouseDown(true);
    }, [isVisible, content]);

    useClickOutside(contentRef, () => {
        if (!frozen) {
            setIsVisible(false);
            onClose && onClose();
            setTargetPosition(undefined);
        }
    });

    return (
        <>
            <div
                className={css.popoverTarget}
                onMouseDown={() => setVisibilityChangedSinceMouseDown(false)}
                onClick={toggle}
                ref={targetRef}
            >
                {children}
            </div>
            {isVisible &&
                createPortal(
                    <div className={css.popoverWrapper} ref={contentRef} style={positionStyle}>
                        <div
                            className={clsx(css.popover, color && css[color], padding && css[`padding-${padding}`])}
                            onClick={() => !ignoreClicks && setIsVisible(false)}
                        >
                            {content}
                        </div>
                        <div
                            className={clsx(
                                css.triangle,
                                !isTriangleHidden && color && css[color],
                                isTriangleHidden && css.transparent,
                                className ? className : css[popoverPlacement],
                            )}
                        />
                    </div>,
                    document.body,
                )}
        </>
    );
};

const PopoverMenu: React.FC<MenuProps> = ({ children, direction = 'column' }) => (
    <div className={clsx(css.menu, direction && css[`direction-${direction}`])}>{children}</div>
);

const PopoverMenuItem: React.FC<MenuItemProps> = ({ children, onClick, beforeContent, afterContent, className }) => (
    <div className={clsx(css.item, className && className)} onClick={(e) => onClick(e)}>
        {beforeContent}
        <Typography color="inherit">{children}</Typography>
        {afterContent}
    </div>
);

Popover.Menu = PopoverMenu;

Popover.MenuItem = PopoverMenuItem;

export default Popover;
